import {
  CssBaseline,
  ThemeProvider,
  type LinkProps,
  type PaletteMode,
  type SxProps,
  createTheme,
  responsiveFontSizes,
} from "@mui/material";
import { csCZ } from '@mui/material/locale';
import { csCZ as pickerLocale } from "@mui/x-date-pickers/locales";
import {
  Link as RouterLink,
  type LinkProps as RouterLinkProps,
} from "react-router-dom";
import * as React from "react";

const LinkBehavior = React.forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, "to"> & { href: RouterLinkProps["to"] }
>((props, ref) => {
  const { href, ...other } = props;
  // Map href (Material UI) -> to (react-router)
  return <RouterLink ref={ref} to={href} {...other} />;
});

export const primaryColor = "#7b8a27";
export const disabledInputTextColor = "rgba(0, 0, 0, 0.4)";
export const COLOR_MODE_STORAGE_KEY = "energovizor-color-mode-v1";

const darkPalette = {
  background: "#101411",
  surface: "#171D18",
  raised: "#202820",
  textPrimary: "#F3F6F1",
  textSecondary: "#B8C2B7",
  divider: "#334034",
} as const;

const generateVariants = (
  variantNames: string[],
  variant: SxProps
) =>
  variantNames.map((variantName) => ({
    props: { variant: variantName },
    style: variant,
  }));

export const createAppTheme = (mode: PaletteMode) => responsiveFontSizes(createTheme({
  palette: {
    mode,
    primary: {
      main: primaryColor,
      contrastText: mode === "dark" ? darkPalette.background : "#fff",
    },
    text: {
      primary: mode === "dark" ? darkPalette.textPrimary : "#58595B",
      ...(mode === "dark" ? { secondary: darkPalette.textSecondary } : {}),
    },
    background: {
      default: mode === "dark" ? darkPalette.background : "#F5F5F5",
      paper: mode === "dark" ? darkPalette.surface : "#fff",
    },
    ...(mode === "dark" ? {
      divider: darkPalette.divider,
      action: {
        hover: "rgba(243, 246, 241, 0.08)",
        selected: "rgba(123, 138, 39, 0.22)",
        disabled: "rgba(184, 194, 183, 0.48)",
        disabledBackground: "rgba(243, 246, 241, 0.10)",
      },
    } : {}),
  },
  typography: {
    fontFamily: 'Outfit, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif'
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          colorScheme: mode,
        },
        body: {
          transition: "background-color 160ms ease, color 160ms ease",
        },
        ".MuiChartsAxis-tickLabel, .MuiChartsLegend-label": {
          fill: mode === "dark" ? darkPalette.textSecondary : "#58595B",
          color: mode === "dark" ? darkPalette.textSecondary : "#58595B",
        },
        ".MuiChartsAxis-line, .MuiChartsAxis-tick, .MuiChartsGrid-line": {
          stroke: mode === "dark" ? darkPalette.divider : "rgba(0, 0, 0, 0.23)",
        },
        ".MuiChartsTooltip-paper": {
          backgroundColor: mode === "dark" ? darkPalette.raised : "#fff",
          color: mode === "dark" ? darkPalette.textPrimary : "#58595B",
        },
        "@media (prefers-reduced-motion: reduce)": {
          "*, *::before, *::after": {
            scrollBehavior: "auto !important",
            animationDuration: "0.01ms !important",
            animationIterationCount: "1 !important",
            transitionDuration: "0.01ms !important",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: mode === "dark" ? darkPalette.divider : undefined,
        },
        head: {
          color: mode === "dark" ? darkPalette.textPrimary : undefined,
        },
      },
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      } as LinkProps,
      styleOverrides: {
        root: {
          fontWeight: "500",
          textDecoration: "none",
        },
      },
      variants: [
        {
          props: { variant: "filled" },
          style: {
            backgroundColor: mode === "dark" ? darkPalette.raised : "white",
            color: `${primaryColor} !important`,
            borderRadius: "5px",
          },
        },
      ],
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: "500",
          borderRadius: "15px",
        },
        containedPrimary: {
          color: `${mode === "dark" ? darkPalette.background : "#fff"} !important`,
        },
      },
      variants: [
        {
          props: { variant: 'white-green' },
          style: {
            backgroundColor: mode === "dark" ? darkPalette.raised : "white",
            color: primaryColor,
            borderRadius: '38px',
            width: '12rem',
            height: '3rem',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '1.2rem',
            ":hover": {
              backgroundColor: primaryColor,
              color: mode === "dark" ? darkPalette.background : '#fff'
            }
          }
        }
      ]
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontWeight: "300",
          wordWrap: "break-word",
        },
      },
      //@ts-expect-error Adding custom variant
      variants: [
        ...generateVariants(["h1", "h2", "h3"], {
          fontWeight: "600",
        }),
      ],
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontWeight: "300",
        },
        input: {
          '&::placeholder': {
            color: mode === 'dark' ? darkPalette.textSecondary : '#000',
            opacity: 1,
            fontWeight: 400
          },
          '&:disabled': {
            color: mode === 'dark' ? darkPalette.textSecondary : disabledInputTextColor,
            WebkitTextFillColor: mode === 'dark' ? darkPalette.textSecondary : disabledInputTextColor,
            WebkitTextStroke: 'none',
          },
        }
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: "300",
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontWeight: 400,
          textTransform: 'none',
          color: mode === 'dark' ? darkPalette.textPrimary : '#000'
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          marginLeft: '5px',
          marginRight: '5px',
          borderRadius: 15,
          ':hover': {
            backgroundColor: primaryColor + '80',
            color: '#fff',
          },
          "&.Mui-selected": {
            backgroundColor: primaryColor,
            color: mode === "dark" ? darkPalette.background : '#fff',
            ':hover': {
              backgroundColor: primaryColor
            }
          },
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'dark' ? darkPalette.raised : '#f5f5f538',
        }
      }
    }
  },
}, csCZ, pickerLocale));

// Compatibility export for non-render-time uses such as breakpoint constants.
export const theme = createAppTheme("light");

type ColorModeContextValue = {
  mode: PaletteMode;
  toggleColorMode: () => void;
};

const ColorModeContext = React.createContext<ColorModeContextValue | null>(null);

const getInitialMode = (): PaletteMode => {
  const stored = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

export const useColorMode = () => {
  const context = React.useContext(ColorModeContext);
  if (!context) throw new Error("useColorMode must be used within AppThemeProvider");
  return context;
};

export const AppThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [mode, setMode] = React.useState<PaletteMode>(getInitialMode);
  const activeTheme = React.useMemo(() => createAppTheme(mode), [mode]);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemModeChange = (event: MediaQueryListEvent) => {
      if (localStorage.getItem(COLOR_MODE_STORAGE_KEY)) return;
      setMode(event.matches ? "dark" : "light");
    };
    mediaQuery.addEventListener("change", handleSystemModeChange);
    return () => mediaQuery.removeEventListener("change", handleSystemModeChange);
  }, []);

  React.useEffect(() => {
    let themeColorMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!themeColorMeta) {
      themeColorMeta = document.createElement("meta");
      themeColorMeta.name = "theme-color";
      document.head.appendChild(themeColorMeta);
    }
    themeColorMeta.content = mode === "dark" ? darkPalette.background : "#F5F5F5";
  }, [mode]);

  const contextValue = React.useMemo<ColorModeContextValue>(() => ({
    mode,
    toggleColorMode: () => {
      setMode((current) => {
        const next = current === "light" ? "dark" : "light";
        localStorage.setItem(COLOR_MODE_STORAGE_KEY, next);
        return next;
      });
    },
  }), [mode]);

  return (
    <ColorModeContext.Provider value={contextValue}>
      <ThemeProvider theme={activeTheme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    filled: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    'white-green': true;
  }
}
